import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import EventTypeSelection from './EventTypeSelection';
import EventDetailsTab from './EventDetailsTab';
import EventTicketsTab from './EventTicketsTab';
import EventSettingsTab from './EventSettingsTab';
import EventOtherDetailsTab from './EventOtherDetailsTab';
import { CountryData, EventFormData } from './types';

import './CreateNewEventV2.css';
import { createEventDraft, createQuickEventV2, getAllCountries, getAllCategories } from '../../../api';
import { Spinner } from 'reactstrap';
import Background from '../../Login/Background';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import { Sparkles, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { useFreeEventLimit } from '../../../hooks/useFreeEventLimit';
import FreeEventLimitNotice from '../../../components/common/FreeEventLimitNotice/FreeEventLimitNotice';

const CreateNewEventV2: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isFreeBooking = new URLSearchParams(location.search).get('freeBooking') === 'true';
  const freeEventLimit = useFreeEventLimit(isFreeBooking);
  
  // State for event type selection
  const [eventTypeSelected, setEventTypeSelected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // State for active tab
  const [activeTab, setActiveTab] = useState(1);
  const [countries, setCountries] = useState<CountryData[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [error, setError] = useState<string>('');
  // State for validation
  const [tabValidation, setTabValidation] = useState({
    detailsTab: false,
    ticketsTab: false,
    settingsTab: false,
    otherDetailsTab: true // Optional tab, always valid
  });
  
  // State for form data
  const [formData, setFormData] = useState<EventFormData>({
    // Details tab
    eventName: '',
    categories: [],
    eventDate: '',
    eventTime: '10:00',
    eventEndTime: '10:00',
    isSingleDayEvent: true,
    endDate:"",
    duration: '',
    eventImage: null,
    eventDescription: '',
    shortDescription: '',
    eventLocation: '',
    venue: '',
    venueDetails:{
      id: '',
      name: '',
      addressLine1: '',
      addressLine2: '',
      country: '',
      state: '',
      city: '',
      zipCode: '',
      latitude: undefined,
      longitude: undefined,
      formattedAddress: '',
    },
    isOnlineEvent: false, // Default to offline event
    hasMultipleVenues: false,
    venues: [],
    
    // Tickets tab
    tickets: [],
    hasMultipleTicketGroups: false,
    ticketGroups: [],
    isTicketFree: isFreeBooking,
    absorbFees: false,

    // Settings tab
    showOtherFees: !isFreeBooking,
    isOtherAttendeeRequired: false,
    hasTermsAndConditions:false,
    termsAndConditions:'',
    templateType:'',
    
    // Other Details tab
    agenda: [],
    lineup: [],
    sponsors: [],
  });

  console.log('formData', formData);

  useEffect(() => {
    const getInitialData = async () => {
      try {
        const response = await getAllCountries();
        console.log("getAllCountries response", response);
        setCountries(response);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
      try {
        const response = await getAllCategories();
        console.log("getAllCategories response", response);
        setCategories(response);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    getInitialData();
  }, []);


  // Handle event type selection
  const handleEventTypeSelect = (isOnlineEvent: boolean) => {
    handleInputChange('isOnlineEvent', isOnlineEvent);
    setEventTypeSelected(true);
  };

  // Handle file upload
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    handleInputChange('eventImage', file);
  };

  // Initialize validation on component mount
  React.useEffect(() => {
  updateValidation(formData);
}, [formData]);

  // Get current tab validation status
  const getCurrentTabValidation = (): boolean => {
    switch (activeTab) {
      case 1:
        return tabValidation.detailsTab;
      case 2:
        return tabValidation.ticketsTab;
      case 3:
        return tabValidation.settingsTab;
      case 4:
        return tabValidation.otherDetailsTab;
      default:
        return false;
    }
  };

  // Validation functions
  const validateDetailsTab = (data: EventFormData): boolean => {
    const requiredFields = [
      data.eventName,
      data.eventDate,
      data.eventImage,
      data.eventTime,
      data.eventDescription,
      data.shortDescription
    ];

    if (!data.isSingleDayEvent) {
      requiredFields.push(data.endDate || '');
      requiredFields.push(data.eventEndTime);
    }

    // Check if venue information is complete (only for offline events)
    let venueValid = true;
    if (!data.isOnlineEvent) {
      if (data.hasMultipleVenues) {
        venueValid = data.venues.length > 0 && data.venues.every(venue => 
          venue.name && venue.formattedAddress && venue.country
        );
      } else {
        venueValid = !!(data.venueDetails.name && data.venueDetails.addressLine1 && data.venueDetails.country);
      }
    }
    const requiredFieldsStatus = requiredFields.every(field => field && field.toString().trim() !== '');
    const categoryValid = data.categories && data.categories.length > 0;
    console.log('requiredFieldsStatus', requiredFieldsStatus);
    console.log('venueValid', venueValid);
    console.log('categoryValid', categoryValid);
    return requiredFieldsStatus && venueValid && categoryValid;
  };

  const validateTicketsTab = (data: EventFormData): boolean => {
    // Must have at least one ticket
    if (data.tickets.length === 0) return false;

    // If multiple ticket groups enabled, must have at least one group
    if (data.hasMultipleTicketGroups && data.ticketGroups.length === 0) return false;

    // All tickets must have required fields
    return data.tickets.every(ticket => 
      ticket.name 
    );
  };

  const validateSettingsTab = (data: EventFormData): boolean => {
    if(data.templateType==='corporate'){
      return false;
    }
    // Basic settings validation - can be expanded based on requirements
    return true; // Settings tab is usually optional
  };

  // Update validation when form data changes
  const updateValidation = (data: EventFormData) => {
    console.log('data', data);
    console.log('validateDetailsTab(data)', validateDetailsTab(data));
    console.log('validateTicketsTab(data)', validateTicketsTab(data));
    console.log('validateSettingsTab(data)', validateSettingsTab(data));
    setTabValidation({
      detailsTab: validateDetailsTab(data),
      ticketsTab: validateTicketsTab(data),
      settingsTab: validateSettingsTab(data),
      otherDetailsTab: true // Optional tab, always valid
    });
  };

  const handleInputChange = <K extends keyof EventFormData>(
    field: K,
    valueOrUpdater: EventFormData[K] | ((prevFieldValue: EventFormData[K]) => EventFormData[K])
  ) => {
    setFormData(prev => {
      const nextFieldValue =
        typeof valueOrUpdater === 'function'
          ? (valueOrUpdater as (v: EventFormData[K]) => EventFormData[K])(prev[field])
          : valueOrUpdater;
  
      const next = { ...prev, [field]: nextFieldValue };
  
      // good for debugging
      console.log('[handleInputChange]', field, { prev, next });
  
      return next;
    });
  };
  
  // keep validation in one place so it always runs on the latest data
  React.useEffect(() => {
    updateValidation(formData);
  }, [formData]);
  

  

  // Navigation functions
  const nextTab = () => {
    if (activeTab < 4) {
      setActiveTab(activeTab + 1);
      // Scroll to top of page after tab change
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const prevTab = () => {
    if (activeTab > 1) {
      setActiveTab(activeTab - 1);
      // Scroll to top of page after tab change
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };
  const canAccessTab = (targetTab: number) => {
    if (targetTab <= activeTab) return true; 
    if (targetTab > 1 && !tabValidation.detailsTab) return false;
    if (targetTab > 2 && !tabValidation.ticketsTab) return false;
    if (targetTab > 3 && !tabValidation.settingsTab) return false;

    return true;
  };

  const goToTab = (targetTab: number, e?: React.MouseEvent) => {
    e?.preventDefault();

    if (canAccessTab(targetTab)) {
      setError('');
      setActiveTab(targetTab);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
      return;
    }
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };



  const finishEvent = async () => {
  if (isFreeBooking && (freeEventLimit.isLoading || freeEventLimit.error || freeEventLimit.remaining === 0)) {
    setError(freeEventLimit.error || 'Limit reached. You cannot create another free event.');
    return;
  }
  try {
    setIsLoading(true);

    const fd = new FormData();

    const venueDetails = formData.hasMultipleVenues && formData.venues.length > 0
      ? formData.venues[0]
      : formData.venueDetails;

    // countryId
    const selectedCountry = countries.find(c =>
      c.countryName.toLowerCase().includes(venueDetails?.country?.toLowerCase() || '')
    );
    fd.append('countryId', selectedCountry?.countryId?.toString() || '7');

    // primitive fields
    fd.append('eventName', formData.eventName || '');
    fd.append('eventDate', formData.eventDate || '');
    fd.append('endDate', formData.isSingleDayEvent ? (formData.eventDate || '') : (formData.endDate || formData.eventDate || ''));
    fd.append('eventTime', formData.eventTime || '');
    fd.append('eventEndTime', formData.eventEndTime || '');
    fd.append('isSingleDayEvent', String(formData.isSingleDayEvent));
    fd.append('isSameDayEvent', String(formData.isSingleDayEvent));
    fd.append('duration', formData.duration || '');
    fd.append('eventDescription', formData.eventDescription || '');
    fd.append('shortDescription', formData.shortDescription || '');
    fd.append('eventLocation', venueDetails?.formattedAddress || venueDetails?.name || '');
    fd.append('venue', venueDetails?.formattedAddress || venueDetails?.name || '');
    fd.append('isOnlineEvent', String(formData.isOnlineEvent));
    fd.append('hasMultipleVenues', String(formData.hasMultipleVenues));
    fd.append('hasMultipleTicketGroups', String(formData.hasMultipleTicketGroups));
    fd.append('isTicketFree', String(formData.isTicketFree));
    fd.append('absorbFees', String(formData.absorbFees));
    fd.append('showOtherFees', formData.isTicketFree ? String(false) : formData.absorbFees ? String(false) : String(formData.showOtherFees));
    fd.append('isOtherAttendeeRequired', String(formData.isOtherAttendeeRequired));
    fd.append('hasTermsAndConditions', String(formData.hasTermsAndConditions));
    fd.append('termsAndConditions', formData.termsAndConditions || '');
    fd.append('templateType', 'corporate');
    const selectedCategoryId = formData.categories?.[0] || '';
    fd.append('categoryId', selectedCategoryId);
    const selectedCategoryObj = categories.find(cat => String(cat.categoryId) === selectedCategoryId);
    if (selectedCategoryObj) {
      fd.append('Category', JSON.stringify({
        categoryId: String(selectedCategoryObj.categoryId),
        categoryName: selectedCategoryObj.categoryName
      }));
    }
    fd.append('categories', JSON.stringify(formData.categories || []));
    fd.append("paymentChannel", "our");
    fd.append("platformFee", "1");
    fd.append("userType", "organizer");
    fd.append("sourceOfCreation","web");
    fd.append("eventType", formData.isOnlineEvent ? "online" : "offline");
    fd.append("onlineEventType", formData.isOnlineEvent ? "online" : "offline");
    fd.append("eventStatus", "active");
    fd.append("eventVenue", venueDetails?.name || '');
    fd.append("eventAddress1", venueDetails?.formattedAddress || venueDetails?.name || '');
    
    // event hero image
    if (formData.eventImage instanceof File) {
      fd.append('eventImage', formData.eventImage, formData.eventImage.name);
    }

    // complex objects/arrays
    fd.append('venueDetails', JSON.stringify(!formData.hasMultipleVenues ? (venueDetails || {}) : {}));

    const locationData = formData.hasMultipleVenues ? formData.venues.map(venue => ({
      name: venue?.name || '',
      venueName: venue?.venueName || venue?.name || '',
      addressLine1: venue?.addressLine1 || venue?.formattedAddress || '',
      addressLine2: venue?.addressLine2 || '',
      city: venue?.city || '',
      state: venue?.state || '',
      country: venue?.country || '',
      zipCode: venue?.zipCode || '',
      latitude: venue?.latitude,
      longitude: venue?.longitude,
      formattedAddress: venue?.formattedAddress || venue?.addressLine1 || venue?.name || '',
    })) : [{
      name: venueDetails?.name || '',
      venueName: venueDetails?.venueName || venueDetails?.name || '',
      addressLine1: venueDetails?.addressLine1 || venueDetails?.formattedAddress || '',
      addressLine2: venueDetails?.addressLine2 || '',
      city: venueDetails?.city || '',
      state: venueDetails?.state || '',
      country: venueDetails?.country || '',
      zipCode: venueDetails?.zipCode || '',
      latitude: venueDetails?.latitude,
      longitude: venueDetails?.longitude,
      formattedAddress: venueDetails?.formattedAddress || venueDetails?.addressLine1 || venueDetails?.name || '',
    }];
    fd.append('locationData', JSON.stringify(locationData));

    fd.append('venues', JSON.stringify(formData.venues || []));
    fd.append('tickets', JSON.stringify(formData.tickets || []));
    fd.append('ticketGroups', JSON.stringify(formData.ticketGroups || []));

    // ---------- AGENDA ----------
const agendaPayload = (formData.agenda || []).map((a, i) => {
  const row: any = { ...a };

  if (a.hostImage instanceof File) {
    // Append actual file under the same field name
    fd.append("hostImage", a.hostImage, a.hostImage.name);

    // Add metadata so backend can match
    row.hostImageKey = a.hostImage.name;
    row.hostImageIndex = i;
  }

  // Don’t send raw file/blob URL in JSON
  delete row.hostImage;
  delete row.hostImageUrl;

  return row;
});
fd.append("agenda", JSON.stringify(agendaPayload));

// ---------- LINEUP ----------
const lineupPayload = (formData.lineup || []).map((l, i) => {
  const row: any = { ...l };

  if (l.artistImage instanceof File) {
    fd.append("artistImage", l.artistImage, l.artistImage.name);
    row.artistImageKey = l.artistImage.name;
    row.artistImageIndex = i;
  }

  delete row.artistImage;
  delete row.artistImageUrl;

  return row;
});
fd.append("lineup", JSON.stringify(lineupPayload));

// ---------- SPONSORS ----------
const sponsorsPayload = (formData.sponsors || []).map((s, i) => {
  const row: any = { ...s };

  if (s.thumb instanceof File) {
    fd.append("thumb", s.thumb, s.thumb.name);
    row.thumbImageKey = s.thumb.name;
    row.thumbImageIndex = i;
  }

  delete row.thumb;
  delete row.thumbImageUrl;

  return row;
});
fd.append("sponsors", JSON.stringify(sponsorsPayload));

     const response = await createQuickEventV2(fd);
    //const response = await createEventDraft(formData);
    
    console.log('response-createEvent', response);
    
    // Extract event ID from response (adjust based on your API response structure)
       const eventId = response?.event?.eventId || response?.eventId || `event_${Date.now()}`;
    //const eventSlug=response?.draftId;
    
    // Redirect to event preview page with event ID as parameter
    navigate(`/events/${eventId}/preview`);
  } catch (error: any) {
    setError(error?.response?.data?.error || 'Something went wrong');
    console.error('Error creating event:', error);
  } finally {
    setIsLoading(false);
  }
};

if (isFreeBooking && !freeEventLimit.isLoading && freeEventLimit.remaining === 0) {
  return <Navigate to="/free-event-limit-reached" replace />;
}

 

return (
    <>
      <Background />
      <div className="create-event-container create-event-v2-page v3-page-transition">
        <div className="container" style={{ maxWidth: 1200, margin: '0 auto' }}>
          <PageHeader
            title={isFreeBooking ? "Create Free Event" : "Create Event Studio"}
            subtitle={isFreeBooking ? "Configure your free event details, ticket tiers, and settings." : "Configure your event details, ticket tiers, and settings."}
            icon={Sparkles}
            actions={
              <div style={{ display: 'flex', background: 'rgba(18, 10, 24, 0.75)', padding: 4, borderRadius: 30, border: '1px solid rgba(255,255,255,0.08)' }}>
                <button
                  type="button"
                  onClick={() => navigate(isFreeBooking ? '/create-quick-event?freeBooking=true' : '/create-quick-event')}
                  style={{
                    padding: '7px 18px', borderRadius: 20, border: 'none',
                    background: 'transparent', color: 'rgba(255,255,255,0.5)',
                    fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#ffffff')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
                >
                  Quick Event (2 min)
                </button>
                <button
                  type="button"
                  style={{
                    padding: '7px 18px', borderRadius: 20, border: 'none',
                    background: 'linear-gradient(135deg, #EE3F7C, #F79B59)', color: '#fff',
                    fontSize: 12, fontWeight: 700, cursor: 'default',
                    boxShadow: '0 4px 12px rgba(238,63,124,0.3)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Full Event Studio
                </button>
              </div>
            }
          />
          {isFreeBooking && <FreeEventLimitNotice limitInfo={freeEventLimit} />}
          {/* Main Content */}
          <div className="event-dt-block create-event-v2-studio">
                      {/* Tab Navigation */}
                      <div style={{ display: "flex", overflowX: "auto", borderBottom: "1px solid rgba(255,255,255,0.06)", paddingBottom: 1, gap: 12, marginBottom: 24 }}>
                        {[1, 2, 3, 4].map(i => {
                          const label = i === 1 ? '1. Details & Venues' : i === 2 ? '2. Ticket Architect' : i === 3 ? '3. Advanced Controls' : '4. Other Details';
                          const isActive = activeTab === i;
                          const accessible = canAccessTab(i) && !isLoading;

                          return (
                            <button
                              key={i}
                              type="button"
                              className={`v3-sub-tab ${isActive ? 'active' : ''}`}
                              onClick={(e) => goToTab(i, e)}
                              disabled={!accessible}
                              style={{
                                opacity: !accessible ? 0.5 : 1,
                                cursor: !accessible ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      {/* Tab Content */}
                      <div className="step-content">
                        {!getCurrentTabValidation() && (
                          <div className="validation-message alert alert-warning mb-3 " role="alert">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Please complete all required fields before proceeding to the next step.
                          </div>
                        )}
                        {/* Details Tab */}
                        {activeTab === 1 && (
                          <EventDetailsTab 
                            formData={formData}
                            onInputChange={handleInputChange}
                            onImageUpload={handleImageUpload}
                            activeTab={activeTab}
                          />
                        )}

                        {/* Tickets Tab */}
                        {activeTab === 2 && (
                          <EventTicketsTab 
                            tickets={formData.tickets}
                            onInputChange={handleInputChange}
                            activeTab={activeTab}
                            hasMultipleVenues={formData.hasMultipleVenues}
                            venues={formData.venues}
                            hasMultipleTicketGroups={formData.hasMultipleTicketGroups}
                            ticketGroups={formData.ticketGroups}
                            isTicketFree={formData.isTicketFree}
                            absorbFees={formData.absorbFees}
                           
                          />
                        )}

                        {/* Settings Tab */}
                        {activeTab === 3 && (
                          <EventSettingsTab 
                            formData={formData}
                            onInputChange={handleInputChange}
                            activeTab={activeTab}
                            isFreeBooking={isFreeBooking}
                          />
                        )}

                        {/* Other Details Tab */}
                        {activeTab === 4 && (
                          <EventOtherDetailsTab 
                            formData={formData}
                            onInputChange={handleInputChange}
                            activeTab={activeTab}
                          />
                        )}
                      </div>

                      {/* Validation Message */}
                      {!getCurrentTabValidation() && (
                        <div
                          style={{
                            padding: "12px 16px",
                            borderRadius: 12,
                            background: "rgba(255, 171, 0, 0.1)",
                            border: "1px solid rgba(255, 171, 0, 0.25)",
                            color: "#FFC107",
                            fontSize: 12,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            margin: "20px 0 0 0",
                          }}
                        >
                          <AlertTriangle size={16} color="#FFC107" />
                          <span>Please complete all required fields before proceeding to the next step.</span>
                        </div>
                      )}

                      {/* Navigation Buttons */}
                      <div
                        className="glass-panel"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: 20,
                          padding: "16px 20px",
                        }}
                      >
                        <div>
                          {activeTab > 1 && (
                            <button
                              type="button"
                              className="v3-btn-secondary"
                              onClick={prevTab}
                              disabled={isLoading}
                            >
                              <ChevronLeft size={16} /> Previous
                            </button>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 12 }}>
                          {activeTab < 4 && (
                            <button
                              type="button"
                              className="v3-btn-primary"
                              onClick={nextTab}
                              disabled={!getCurrentTabValidation() || isLoading}
                              style={{
                                opacity: (!getCurrentTabValidation() || isLoading) ? 0.5 : 1,
                                cursor: (!getCurrentTabValidation() || isLoading) ? "not-allowed" : "pointer",
                              }}
                            >
                              Next <ChevronRight size={16} />
                            </button>
                          )}
                          {activeTab === 4 && (
                            <button
                              type="button"
                              className="v3-btn-primary"
                              onClick={finishEvent}
                              disabled={isLoading}
                            >
                              {isLoading ? <Spinner size="sm" color="white" /> : 'Create Event'} <Sparkles size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                      {error && (
                        <div className="alert alert-danger" role="alert">
                          {error}
                        </div>
                      )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateNewEventV2;